import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { ArrowRight, Shield, Truck, Award, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProductCard from '@/components/ProductCard';

export default function HomePage() {
  const { user } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [mysteryBoxes, setMysteryBoxes] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Seed products first
        await axios.post(`${API}/seed-products`).catch(() => {});
        
        // Fetch featured products
        const featuredRes = await axios.get(`${API}/products?featured=true&limit=4`);
        setFeaturedProducts(featuredRes.data);
        
        // Fetch mystery boxes
        const mysteryRes = await axios.get(`${API}/products?category=mystery_box&limit=3`);
        setMysteryBoxes(mysteryRes.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(`Subscribed! Use code ${response.data.discount_code} for 10% off!`);
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe');
    }
  };

  const categories = [
    { name: 'Mystery Boxes', image: 'https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg', href: '/products?category=mystery_box' },
    { name: 'Signed Jerseys', image: 'https://images.unsplash.com/photo-1571461542309-dedaaec54069?w=800', href: '/products?category=jersey' },
    { name: 'Helmets', image: 'https://images.unsplash.com/photo-1632166160997-90bdd7c41e97?w=800', href: '/products?category=helmet' },
    { name: 'Balls', image: 'https://images.unsplash.com/photo-1703114608920-682133cc2ea2?w=800', href: '/products?category=ball' },
  ];

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-glow" data-testid="hero-section">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1655089131279-8029e8a21ac6?w=1920')] bg-cover bg-center opacity-20" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <div className="mb-6 animate-fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_goofy-hawking-1/artifacts/8ew7ku5q_photo-output.png" 
                alt="Pure Shmacks" 
                className="h-32 md:h-40 lg:h-48 w-auto object-contain"
              />
            </div>
            <h2 className="heading-display text-3xl md:text-4xl lg:text-5xl text-white mb-4 animate-fade-in stagger-1">
              Premium Sports Memorabilia
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-xl animate-fade-in stagger-2">
              Authenticated autographs from your favorite athletes. Every item verified by JSA, Beckett, or PSA. 100% Authenticity Guaranteed.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in stagger-3">
              <Link to="/products" data-testid="shop-now-btn">
                <Button className="btn-primary flex items-center gap-2">
                  Shop Now <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/products?category=mystery_box" data-testid="mystery-boxes-btn">
                <Button className="btn-secondary">
                  Mystery Boxes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 border-y border-[#27272A] bg-[#0A0A0A]">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <Shield className="w-10 h-10 text-[#C41E3A]" />
              <div>
                <h4 className="font-barlow font-bold text-white">Authenticity Guarantee</h4>
                <p className="text-white/50 text-sm">Every item verified & certified</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Truck className="w-10 h-10 text-[#C41E3A]" />
              <div>
                <h4 className="font-barlow font-bold text-white">Free Shipping $250+</h4>
                <p className="text-white/50 text-sm">U.S. orders only</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Award className="w-10 h-10 text-[#C41E3A]" />
              <div>
                <h4 className="font-barlow font-bold text-white">Premium Quality</h4>
                <p className="text-white/50 text-sm">Hand-selected items</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="section-gap" data-testid="categories-section">
        <div className="container-main">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-caps text-[#C41E3A] mb-2 block">Browse Collection</span>
              <h2 className="heading-display text-3xl md:text-4xl text-white">Shop By Category</h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-200">
              View All <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.name}
                to={category.href}
                className={`relative group overflow-hidden ${index === 0 ? 'col-span-2 row-span-2' : ''}`}
                data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`aspect-square ${index === 0 ? 'md:aspect-auto md:h-full' : ''}`}>
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-6 left-6">
                    <h3 className="font-barlow font-black text-xl md:text-2xl uppercase text-white group-hover:text-[#C41E3A] transition-colors duration-300">
                      {category.name}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mystery Boxes Section */}
      {mysteryBoxes.length > 0 && (
        <section className="section-gap bg-[#0A0A0A]" data-testid="mystery-boxes-section">
          <div className="container-main">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="label-caps text-[#C41E3A] mb-2 block">Chase Your Grail</span>
                <h2 className="heading-display text-3xl md:text-4xl text-white">Mystery Boxes</h2>
                <p className="text-white/60 mt-2">Hand-numbered editions with legendary hits inside</p>
              </div>
              <Link to="/mystery-boxes" className="hidden md:flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-200">
                View All <ChevronRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mysteryBoxes.filter(p => !p.coming_soon).slice(0, 3).map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>

            <div className="mt-8 text-center md:hidden">
              <Link to="/mystery-boxes">
                <Button className="btn-secondary">View All Mystery Boxes</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Hits Gallery Teaser */}
      <section className="py-16 border-y border-[#27272A]" data-testid="hits-gallery-teaser">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <span className="label-caps text-[#C41E3A] mb-2 block">Real Pulls, Real Excitement</span>
              <h2 className="heading-display text-2xl md:text-3xl text-white mb-2">See What Others Have Pulled!</h2>
              <p className="text-white/60">Watch unboxing videos and see the incredible hits from our mystery boxes</p>
            </div>
            <Link to="/hits-gallery">
              <Button className="btn-primary flex items-center gap-2">
                View Hits Gallery <ChevronRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="section-gap" data-testid="featured-products-section">
          <div className="container-main">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="label-caps text-[#C41E3A] mb-2 block">Top Picks</span>
                <h2 className="heading-display text-3xl md:text-4xl text-white">Featured Items</h2>
              </div>
              <Link to="/products?featured=true" className="hidden md:flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-200">
                View All <ChevronRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="section-gap bg-[#C41E3A]" data-testid="newsletter-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-display text-3xl md:text-5xl text-white mb-4">Get 10% Off</h2>
            <p className="text-white/80 mb-8">
              Subscribe to our newsletter and receive an exclusive discount code for your first order.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12 flex-1"
                data-testid="hero-newsletter-input"
              />
              <Button type="submit" className="bg-white text-[#C41E3A] hover:bg-white/90 font-barlow font-bold uppercase h-12 px-8" data-testid="hero-newsletter-submit">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Wholesale & Sell CTA */}
      <section className="section-gap" data-testid="cta-section">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/wholesale" className="group block bg-[#0A0A0A] border border-[#27272A] p-8 md:p-12 hover:border-[#C41E3A] transition-colors duration-300" data-testid="wholesale-cta">
              <span className="label-caps text-[#007AFF] mb-4 block">For Business</span>
              <h3 className="heading-display text-2xl md:text-3xl text-white mb-4 group-hover:text-[#C41E3A] transition-colors duration-300">
                Wholesale Partners
              </h3>
              <p className="text-white/60 mb-6">
                Join our wholesale program and get access to exclusive pricing and inventory.
              </p>
              <span className="inline-flex items-center gap-2 text-white font-barlow font-bold uppercase group-hover:text-[#C41E3A] transition-colors duration-300">
                Apply Now <ArrowRight className="w-5 h-5" />
              </span>
            </Link>

            <Link to="/sell-to-us" className="group block bg-[#0A0A0A] border border-[#27272A] p-8 md:p-12 hover:border-[#C41E3A] transition-colors duration-300" data-testid="sell-to-us-cta">
              <span className="label-caps text-[#007AFF] mb-4 block">Got Memorabilia?</span>
              <h3 className="heading-display text-2xl md:text-3xl text-white mb-4 group-hover:text-[#C41E3A] transition-colors duration-300">
                Sell To Us
              </h3>
              <p className="text-white/60 mb-6">
                Have authenticated sports memorabilia? We're always looking to buy quality items.
              </p>
              <span className="inline-flex items-center gap-2 text-white font-barlow font-bold uppercase group-hover:text-[#C41E3A] transition-colors duration-300">
                Get Started <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
